/**
 * Hook: useChatSession
 * Manages chat session lifecycle
 */

import { useState, useCallback, useEffect } from 'react';
import { createSession, getChatHistory } from '../utils/chat-api-client.js';

export function useChatSession(worldId) {
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [messages, setMessages] = useState([]);

  // Initialize session
  const initializeSession = useCallback(async (identifier = 'anonymous') => {
    setLoading(true);
    setError(null);

    try {
      const session = await createSession(worldId, identifier);
      setSessionId(session.session_id);

      // Fetch initial history (should be empty)
      const history = await getChatHistory(session.session_id);
      setMessages(history);
    } catch (err) {
      setError(err.message || 'Failed to create session');
      console.error('Session initialization error:', err);
    } finally {
      setLoading(false);
    }
  }, [worldId]);

  // Load chat history
  const loadHistory = useCallback(async () => {
    if (!sessionId) return;

    try {
      const history = await getChatHistory(sessionId);
      setMessages(history);
    } catch (err) {
      console.error('Error loading history:', err);
    }
  }, [sessionId]);

  // Add local message to state (optimistic update)
  const addLocalMessage = useCallback((role, content) => {
    const stableId = `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const newMessage = {
      message_id: stableId, // This won't change even after server update
      role,
      content,
      created_at: new Date().toISOString(),
      rag_sources: null,
    };
    setMessages((prev) => [...prev, newMessage]);
    return newMessage;
  }, []);

  // Update message with server response
  const updateMessage = useCallback((messageId, updates) => {
    setMessages((prev) =>
      prev.map((msg) => (msg.message_id === messageId ? { ...msg, ...updates } : msg))
    );
  }, []);

  return {
    sessionId,
    messages,
    loading,
    error,
    initializeSession,
    loadHistory,
    addLocalMessage,
    updateMessage,
    setError,
  };
}
